import { motion } from "framer-motion";
import { Fuel, Plus } from "lucide-react";
import { useState } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { AddGasStationBrandModal } from "@/components/gas-stations/AddGasStationBrandModal";
import { AddGasStationModal } from "@/components/gas-stations/AddGasStationModal";
import { FuelNetworkAnalytics } from "@/components/gas-stations/FuelNetworkAnalytics";
import { GasStationBrandsSection } from "@/components/gas-stations/GasStationBrandsSection";
import { GasStationsExplorer } from "@/components/gas-stations/GasStationsExplorer";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { useGasStationBrands, useGasStations } from "@/lib/queries";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function GasStationsPage() {
  const { data: stations, isLoading, error } = useGasStations();
  const {
    data: brands = [],
    isLoading: brandsLoading,
    error: brandsError,
  } = useGasStationBrands();
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [stationModalOpen, setStationModalOpen] = useState(false);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <PageHeader
        title="Fuel network"
        description="Search Jamaica's fuel stations, filter by brand and parish, and update E-10 87 prices live from Supabase."
        showGreeting={false}
        action={
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Add gas station"
              onClick={() => setStationModalOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-accent)] text-white opacity-100 transition hover:bg-[var(--color-accent-hover)] focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <Fuel className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Add gas station brand"
              onClick={() => setBrandModalOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-accent)] text-white opacity-100 transition hover:bg-[var(--color-accent-hover)] focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        }
      />

      <FuelMenuNav />

      {isLoading ? (
        <div className="panel overflow-hidden rounded-3xl p-4 sm:p-5">
          <div className="mb-5 h-6 w-40 animate-pulse rounded-full bg-border/50" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-2xl bg-border/40" />
            ))}
          </div>
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load fuel analytics.</p>
      ) : (
        <FuelNetworkAnalytics stations={stations ?? []} />
      )}

      <GasStationBrandsSection
        brands={brands}
        isLoading={brandsLoading}
        error={brandsError}
      />

      {isLoading ? (
        <ExplorerSkeleton title="Fuel network" />
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load stations. Please try again.</p>
      ) : (
        <GasStationsExplorer stations={stations ?? []} />
      )}

      <AddGasStationBrandModal open={brandModalOpen} onClose={() => setBrandModalOpen(false)} />
      <AddGasStationModal open={stationModalOpen} onClose={() => setStationModalOpen(false)} />
    </motion.div>
  );
}
